import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Redirect, useHistory, withRouter } from "react-router-dom";
import auth from '../../../Auth';
import {
  loginRequestBegin,
  setCanAccessAdminDashboard,
  setCanAccessVolunteerDashboard
} from 'features/Authentication/redux/authActions';
import { processAuthResult, handleAuthError } from 'utils/authorize';
import { handleRoleAccess } from 'utils/access';
import {
  HEAD_REFEREE_ID,
  EVENT_ADMIN_ID,
  JUDGE_ADVISOR_ID,
} from 'shared/constants/eventRoleTypes';
import ScreenLoading from '../Spinner/ScreenLoading';

const RequireAuthentication = (WrappedComponent) => {

  const AuthenticatedPage = (props) => {
    const { authorized, loading, location, hasAcceptedTerms } = props;
    const { hash } = location;
    const history = useHistory();
    const [isAuthenticatingUser, setIsAuthenticatingUser] = useState(!authorized);

    useEffect(() => {
      // Check if user is authenticated on mount (for BFF cookie-based auth)
      const checkAuth = async () => {
        if (!authorized) {
          try {
            const authResult = await auth.GetInstance().handleAuthentication();
            const roleData = await processAuthResult(authResult);
            if (!roleData) {
              setIsAuthenticatingUser(false);
              return;
            }

            setIsAuthenticatingUser(false);

            // On page reload, stay on the current page instead of redirecting
            if (location.pathname === '/') {
              handleRoleAccess({
                ...props,
                ...roleData
              });
            } else {
              const { setAdminDashboardAccess, setVolunteerDashboardAccess } = props;
              const { userType, userEventRoles } = roleData;
              setAdminDashboardAccess(
                [HEAD_REFEREE_ID, EVENT_ADMIN_ID, JUDGE_ADVISOR_ID].some(i => userEventRoles.includes(i)) || userType !== 'User'
              );
              setVolunteerDashboardAccess(true);
            }
          } catch (err) {
            setIsAuthenticatingUser(false);
            // Don't show error, just redirect to login
            // User is not authenticated, will be redirected below
          }
        }
      };
      
      checkAuth();
    }, []);

    // With BFF, no need to check expiresAt - cookies and refresh are handled server-side
    // The BFF will automatically refresh tokens when needed

    if (loading || isAuthenticatingUser) {
      return <ScreenLoading loading subTitle='Loading User' />;
    }

    if (authorized) {
      return !hasAcceptedTerms 
       ? <Redirect {...props} to={{ pathname: '/terms-and-conditions' }} />
       : <WrappedComponent />;
    }
    return (
      <Redirect
        {...props}
        to={{
          pathname: '/login',
          state: {
            from: `${location.pathname || ''}${location.search || ''}${location.hash || ''}`,
          },
        }}
      />
    );
  };

  AuthenticatedPage.propTypes = {
    loading: PropTypes.bool.isRequired,
    authorized: PropTypes.bool.isRequired,
    history: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    setVolunteerDashboardAccess: PropTypes.func.isRequired,
    setAdminDashboardAccess: PropTypes.func.isRequired
  };

  const mapStateToProps = (state) => {
    const { auth: { authorized, loading, userId, userInfo: { hasAcceptedTerms } } } = state;
    return {
      authorized,
      loading,
      userId,
      hasAcceptedTerms
    };
  };

  const mapDispatchToProps = {
    loginRequestBegin,
    setAdminDashboardAccess: setCanAccessAdminDashboard,
    setVolunteerDashboardAccess: setCanAccessVolunteerDashboard
  };

  return withRouter(connect(mapStateToProps, mapDispatchToProps)(AuthenticatedPage));
};

export default RequireAuthentication;
